import React, { useState, Fragment, Component } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { LinkIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon, ImageIcon, UserIcon, SparklesIcon, ArrowRightIcon, ArrowLeftIcon, GripVerticalIcon, XIcon, CheckIcon, HomeIcon, MapPinIcon, BedDoubleIcon, RulerIcon, BuildingIcon, EuroIcon, PhoneIcon, MailIcon, UploadIcon, CameraIcon } from 'lucide-react';
// Types
interface PropertyData {
  title: string;
  address: string;
  city: string;
  zone: string;
  propertyType: string;
  rooms: string;
  surface: string;
  floor: string;
  price: string;
  description: string;
  features: string[];
}
interface AgentData {
  name: string;
  phone: string;
  email: string;
  photo: string | null;
  tagline: string;
  showContactHeader: boolean;
  showContactFooter: boolean;
}
interface ImportedPhoto {
  id: string;
  url: string;
  selected: boolean;
}
interface PropertyPresentationWizardProps {
  onComplete?: (presentationId: string) => void;
}
const propertyTypes = ['Apartament', 'Casă / Vilă', 'Garsonieră', 'Penthouse', 'Duplex', 'Spațiu comercial', 'Birou', 'Teren'];
const featureOptions = ['Balcon', 'Terasă', 'Parcare', 'Garaj', 'Mobilat', 'Utilat', 'Centrală proprie', 'Aer condiționat', 'Lift', 'Boxa', 'Piscină', 'Grădină'];
export function PropertyPresentationWizard({
  onComplete
}: PropertyPresentationWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  // Step 1 - Link
  const [listingUrl, setListingUrl] = useState('');
  // Step 2 - Property Data
  const [propertyData, setPropertyData] = useState<PropertyData>({
    title: '',
    address: '',
    city: '',
    zone: '',
    propertyType: 'Apartament',
    rooms: '',
    surface: '',
    floor: '',
    price: '',
    description: '',
    features: []
  });
  const [photos, setPhotos] = useState<ImportedPhoto[]>([]);
  // Step 3 - Agent Data
  const [agentData, setAgentData] = useState<AgentData>({
    name: '',
    phone: '',
    email: '',
    photo: null,
    tagline: '',
    showContactHeader: true,
    showContactFooter: true
  });
  // Step 4 - Generated presentation
  const [presentationId, setPresentationId] = useState<string | null>(null);
  const steps = [{
    number: 1,
    title: 'Import Link',
    icon: LinkIcon
  }, {
    number: 2,
    title: 'Detalii Proprietate',
    icon: HomeIcon
  }, {
    number: 3,
    title: 'Date Agent',
    icon: UserIcon
  }, {
    number: 4,
    title: 'Prezentare',
    icon: SparklesIcon
  }];
  // Simulate import from URL
  const handleImportData = async () => {
    if (!listingUrl.trim()) {
      setImportError('Te rugăm să introduci un link valid.');
      return;
    }
    setIsLoading(true);
    setImportError(null);
    setImportSuccess(false);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Simulate successful import with mock data
    const mockPhotos: ImportedPhoto[] = [{
      id: '1',
      url: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
      selected: true
    }, {
      id: '2',
      url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      selected: true
    }, {
      id: '3',
      url: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800',
      selected: true
    }, {
      id: '4',
      url: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800',
      selected: true
    }, {
      id: '5',
      url: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
      selected: true
    }, {
      id: '6',
      url: 'https://images.unsplash.com/photo-1560185893-a55cbc8c57e8?w=800',
      selected: false
    }];
    setPropertyData({
      title: 'Apartament modern 3 camere - Zona Centrală',
      address: 'Strada Victoriei nr. 45',
      city: 'București',
      zone: 'Sector 1 - Centru',
      propertyType: 'Apartament',
      rooms: '3',
      surface: '85',
      floor: '4/8',
      price: '185000',
      description: 'Apartament spațios și luminos, complet renovat în 2023. Situat într-o zonă liniștită, cu acces facil la transport public și toate facilitățile necesare. Finisaje premium, bucătărie complet utilată, 2 băi moderne. Vedere panoramică spre parc.',
      features: ['Balcon', 'Parcare', 'Mobilat', 'Utilat', 'Centrală proprie', 'Aer condiționat', 'Lift']
    });
    setPhotos(mockPhotos);
    setIsLoading(false);
    setImportSuccess(true);
  };
  const handlePhotoToggle = (photoId: string) => {
    setPhotos(prev => prev.map(p => p.id === photoId ? {
      ...p,
      selected: !p.selected
    } : p));
  };
  const handleFeatureToggle = (feature: string) => {
    setPropertyData(prev => ({
      ...prev,
      features: prev.features.includes(feature) ? prev.features.filter(f => f !== feature) : [...prev.features, feature]
    }));
  };
  const handleGeneratePresentation = async () => {
    setIsLoading(true);
    // Simulate generation
    await new Promise(resolve => setTimeout(resolve, 1500));
    const newPresentationId = `pres-${Date.now()}`;
    setPresentationId(newPresentationId);
    setIsLoading(false);
    setCurrentStep(4);
  };
  const selectedPhotos = photos.filter(p => p.selected);
  const canProceedStep1 = importSuccess;
  const canProceedStep2 = propertyData.title && propertyData.price && selectedPhotos.length > 0;
  const canProceedStep3 = agentData.name && agentData.phone;
  return <div className="min-h-screen bg-[#F5F7FB]" data-id="element-4576">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20" data-id="element-4577">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4" data-id="element-4578">
          <div className="flex items-center justify-between mb-4" data-id="element-4579">
            <div data-id="element-4580">
              <h1 className="text-xl sm:text-2xl font-bold text-[#132C53]" data-id="element-4581">
                Generator Prezentare Proprietate
              </h1>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4582">
                Creează o prezentare profesională branduită CautLocuinta.ro
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-between" data-id="element-4583">
            {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;
            return <Fragment key={step.number} data-id="element-4584">
                  <div className="flex items-center" data-id="element-4585">
                    <motion.div animate={{
                  scale: isActive ? 1.1 : 1,
                  backgroundColor: isCompleted ? '#21A366' : isActive ? '#357BEB' : '#E5E7EB'
                }} className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors ${isCompleted || isActive ? 'text-white' : 'text-gray-500'}`} data-id="element-4586">
                      {isCompleted ? <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-4587" /> : <Icon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-4588" />}
                    </motion.div>
                    <span className={`ml-2 text-sm font-medium hidden sm:block ${isActive ? 'text-[#357BEB]' : isCompleted ? 'text-[#21A366]' : 'text-gray-500'}`} data-id="element-4589">
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && <div className={`flex-1 h-1 mx-2 sm:mx-4 rounded-full transition-colors ${currentStep > step.number ? 'bg-[#21A366]' : 'bg-gray-200'}`} data-id="element-4590" />}
                </Fragment>;
          })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-id="element-4591">
        <AnimatePresence mode="wait" data-id="element-4592">
          {/* Step 1: Import Link */}
          {currentStep === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-4593">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-4594">
                <div className="bg-gradient-to-r from-[#357BEB] to-[#21A366] p-6 sm:p-8 text-white" data-id="element-4595">
                  <div className="flex items-center space-x-3 mb-3" data-id="element-4596">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center" data-id="element-4597">
                      <LinkIcon className="w-6 h-6" data-id="element-4598" />
                    </div>
                    <div data-id="element-4599">
                      <h2 className="text-xl sm:text-2xl font-bold" data-id="element-4600">
                        Importă detalii dintr-un anunț existent
                      </h2>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm sm:text-base" data-id="element-4601">
                    Introdu link-ul cu anunțul proprietății de la cealaltă
                    agenție. Vom prelua automat pozele și informațiile esențiale
                    și vom genera o prezentare branduită CautLocuinta.ro.
                  </p>
                </div>

                <div className="p-6 sm:p-8" data-id="element-4602">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4603">
                    Link anunț proprietate
                  </label>
                  <div className="relative" data-id="element-4604">
                    <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4605" />
                    <input type="url" value={listingUrl} onChange={e => {
                  setListingUrl(e.target.value);
                  setImportError(null);
                  setImportSuccess(false);
                }} placeholder="https://..." className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors text-base" data-id="element-4606" />
                  </div>

                  {importError && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3" data-id="element-4607">
                      <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-4608" />
                      <div data-id="element-4609">
                        <p className="text-red-700 font-medium" data-id="element-4610">
                          {importError}
                        </p>
                        <p className="text-red-600 text-sm mt-1" data-id="element-4611">
                          Poți completa manual detaliile în pasul următor.
                        </p>
                      </div>
                    </motion.div>}

                  {importSuccess && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start space-x-3" data-id="element-4612">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-4613" />
                      <div data-id="element-4614">
                        <p className="text-green-700 font-medium" data-id="element-4615">
                          Am găsit {photos.length} poze și{' '}
                          {Object.keys(propertyData).filter(k => propertyData[k as keyof PropertyData]).length}{' '}
                          câmpuri de date.
                        </p>
                        <p className="text-green-600 text-sm mt-1" data-id="element-4616">
                          Mergi la pasul următor pentru a le verifica și edita.
                        </p>
                      </div>
                    </motion.div>}

                  <button onClick={handleImportData} disabled={isLoading || !listingUrl.trim()} className={`mt-6 w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center space-x-2 ${isLoading || !listingUrl.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-lg hover:scale-[1.02]'}`} data-id="element-4617">
                    {isLoading ? <>
                        <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-4618" />
                        <span data-id="element-4619">Preluăm informațiile...</span>
                      </> : <>
                        <SparklesIcon className="w-5 h-5" data-id="element-4620" />
                        <span data-id="element-4621">Importă datele</span>
                      </>}
                  </button>

                  <p className="text-center text-sm text-gray-500 mt-4" data-id="element-4622">
                    Vom încerca să preluăm titlul, descrierea, pozele și
                    principalele detalii tehnice din pagina respectivă.
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-end mt-6" data-id="element-4623">
                <button onClick={() => setCurrentStep(2)} disabled={!canProceedStep1} className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${canProceedStep1 ? 'bg-[#357BEB] text-white hover:bg-[#2868d8]' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-4624">
                  <span data-id="element-4625">Continuă</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-4626" />
                </button>
              </div>
            </motion.div>}

          {/* Step 2: Property Details */}
          {currentStep === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-4627">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4628">
                {/* Left Column - Form Fields */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6" data-id="element-4629">
                  <h2 className="text-xl font-bold text-[#132C53] mb-6 flex items-center" data-id="element-4630">
                    <HomeIcon className="w-6 h-6 mr-2 text-[#357BEB]" data-id="element-4631" />
                    Verifică și editează informațiile
                  </h2>

                  <div className="space-y-5" data-id="element-4632">
                    {/* Title */}
                    <div data-id="element-4633">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4634">
                        Titlu proprietate *
                      </label>
                      <input type="text" value={propertyData.title} onChange={e => setPropertyData(prev => ({
                    ...prev,
                    title: e.target.value
                  }))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4635" />
                    </div>

                    {/* Address Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4636">
                      <div data-id="element-4637">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4638">
                          Localitate
                        </label>
                        <input type="text" value={propertyData.city} onChange={e => setPropertyData(prev => ({
                      ...prev,
                      city: e.target.value
                    }))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4639" />
                      </div>
                      <div data-id="element-4640">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4641">
                          Zonă
                        </label>
                        <input type="text" value={propertyData.zone} onChange={e => setPropertyData(prev => ({
                      ...prev,
                      zone: e.target.value
                    }))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4642" />
                      </div>
                    </div>

                    {/* Street */}
                    <div data-id="element-4643">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4644">
                        Adresă / Stradă
                      </label>
                      <div className="relative" data-id="element-4645">
                        <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4646" />
                        <input type="text" value={propertyData.address} onChange={e => setPropertyData(prev => ({
                      ...prev,
                      address: e.target.value
                    }))} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4647" />
                      </div>
                    </div>

                    {/* Property Type & Rooms */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4648">
                      <div data-id="element-4649">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4650">
                          Tip proprietate
                        </label>
                        <select value={propertyData.propertyType} onChange={e => setPropertyData(prev => ({
                      ...prev,
                      propertyType: e.target.value
                    }))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors bg-white" data-id="element-4651">
                          {propertyTypes.map(type => <option key={type} value={type} data-id="element-4652">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4653">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4654">
                          Număr camere
                        </label>
                        <div className="relative" data-id="element-4655">
                          <BedDoubleIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4656" />
                          <input type="text" value={propertyData.rooms} onChange={e => setPropertyData(prev => ({
                        ...prev,
                        rooms: e.target.value
                      }))} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4657" />
                        </div>
                      </div>
                    </div>

                    {/* Surface & Floor */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4658">
                      <div data-id="element-4659">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4660">
                          Suprafață (mp)
                        </label>
                        <div className="relative" data-id="element-4661">
                          <RulerIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4662" />
                          <input type="text" value={propertyData.surface} onChange={e => setPropertyData(prev => ({
                        ...prev,
                        surface: e.target.value
                      }))} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4663" />
                        </div>
                      </div>
                      <div data-id="element-4664">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4665">
                          Etaj
                        </label>
                        <div className="relative" data-id="element-4666">
                          <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4667" />
                          <input type="text" value={propertyData.floor} onChange={e => setPropertyData(prev => ({
                        ...prev,
                        floor: e.target.value
                      }))} placeholder="ex: 4/8" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4668" />
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div data-id="element-4669">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4670">
                        Preț (€) *
                      </label>
                      <div className="relative" data-id="element-4671">
                        <EuroIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4672" />
                        <input type="text" value={propertyData.price} onChange={e => setPropertyData(prev => ({
                      ...prev,
                      price: e.target.value
                    }))} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4673" />
                      </div>
                    </div>

                    {/* Description */}
                    <div data-id="element-4674">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4675">
                        Descriere
                      </label>
                      <textarea value={propertyData.description} onChange={e => setPropertyData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))} rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors resize-none" data-id="element-4676" />
                    </div>

                    {/* Features */}
                    <div data-id="element-4677">
                      <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-4678">
                        Facilități
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4679">
                        {featureOptions.map(feature => <button key={feature} onClick={() => handleFeatureToggle(feature)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${propertyData.features.includes(feature) ? 'bg-[#357BEB] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-4680">
                            {feature}
                          </button>)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Photos */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6" data-id="element-4681">
                  <h2 className="text-xl font-bold text-[#132C53] mb-2 flex items-center" data-id="element-4682">
                    <ImageIcon className="w-6 h-6 mr-2 text-[#357BEB]" data-id="element-4683" />
                    Selectează pozele
                  </h2>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-4684">
                    Bifează pozele care vor apărea în prezentare. Trage pentru a
                    reordona.
                  </p>

                  <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200" data-id="element-4685">
                    <p className="text-sm text-blue-700" data-id="element-4686">
                      <strong data-id="element-4687">{selectedPhotos.length}</strong> poze selectate
                      din {photos.length}
                    </p>
                  </div>

                  <Reorder.Group axis="y" values={photos} onReorder={setPhotos} className="space-y-3" data-id="element-4688">
                    {photos.map(photo => <Reorder.Item key={photo.id} value={photo} className={`flex items-center space-x-3 p-2 rounded-xl border-2 transition-colors cursor-grab active:cursor-grabbing ${photo.selected ? 'border-[#357BEB] bg-blue-50' : 'border-gray-200 bg-gray-50'}`} data-id="element-4689">
                        <GripVerticalIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-4690" />
                        <img src={photo.url} alt="" className="w-20 h-14 object-cover rounded-lg flex-shrink-0" data-id="element-4691" />
                        <div className="flex-1 min-w-0" data-id="element-4692">
                          <p className="text-sm font-medium text-gray-700 truncate" data-id="element-4693">
                            Imagine {photo.id}
                          </p>
                        </div>
                        <button onClick={() => handlePhotoToggle(photo.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${photo.selected ? 'bg-[#357BEB] text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`} data-id="element-4694">
                          {photo.selected ? <CheckIcon className="w-5 h-5" data-id="element-4695" /> : <XIcon className="w-5 h-5" data-id="element-4696" />}
                        </button>
                      </Reorder.Item>)}
                  </Reorder.Group>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6" data-id="element-4697">
                <button onClick={() => setCurrentStep(1)} className="flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-4698">
                  <ArrowLeftIcon className="w-5 h-5" data-id="element-4699" />
                  <span data-id="element-4700">Înapoi</span>
                </button>
                <button onClick={() => setCurrentStep(3)} disabled={!canProceedStep2} className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${canProceedStep2 ? 'bg-[#357BEB] text-white hover:bg-[#2868d8]' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-4701">
                  <span data-id="element-4702">Continuă</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-4703" />
                </button>
              </div>
            </motion.div>}

          {/* Step 3: Agent Data */}
          {currentStep === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-4704">
              <div className="max-w-2xl mx-auto" data-id="element-4705">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8" data-id="element-4706">
                  <h2 className="text-xl font-bold text-[#132C53] mb-6 flex items-center" data-id="element-4707">
                    <UserIcon className="w-6 h-6 mr-2 text-[#357BEB]" data-id="element-4708" />
                    Completează datele tale de agent
                  </h2>

                  <div className="space-y-5" data-id="element-4709">
                    {/* Agent Photo */}
                    <div className="flex items-center space-x-4" data-id="element-4710">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#357BEB] to-[#21A366] flex items-center justify-center overflow-hidden" data-id="element-4711">
                        {agentData.photo ? <img src={agentData.photo} alt="" className="w-full h-full object-cover" data-id="element-4712" /> : <UserIcon className="w-10 h-10 text-white" data-id="element-4713" />}
                      </div>
                      <div data-id="element-4714">
                        <p className="text-sm font-semibold text-gray-700 mb-2" data-id="element-4715">
                          Poză agent (opțional)
                        </p>
                        <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors" data-id="element-4716">
                          <UploadIcon className="w-4 h-4" data-id="element-4717" />
                          <span data-id="element-4718">Încarcă poză</span>
                        </button>
                      </div>
                    </div>

                    {/* Agent Name */}
                    <div data-id="element-4719">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4720">
                        Nume agent CLC *
                      </label>
                      <div className="relative" data-id="element-4721">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4722" />
                        <input type="text" value={agentData.name} onChange={e => setAgentData(prev => ({
                      ...prev,
                      name: e.target.value
                    }))} placeholder="ex: Maria Popescu" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4723" />
                      </div>
                    </div>

                    {/* Phone */}
                    <div data-id="element-4724">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4725">
                        Telefon agent *
                      </label>
                      <div className="relative" data-id="element-4726">
                        <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4727" />
                        <input type="tel" value={agentData.phone} onChange={e => setAgentData(prev => ({
                      ...prev,
                      phone: e.target.value
                    }))} placeholder="ex: 0722 123 456" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4728" />
                      </div>
                    </div>

                    {/* Email */}
                    <div data-id="element-4729">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4730">
                        Email agent (opțional)
                      </label>
                      <div className="relative" data-id="element-4731">
                        <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4732" />
                        <input type="email" value={agentData.email} onChange={e => setAgentData(prev => ({
                      ...prev,
                      email: e.target.value
                    }))} placeholder="ex: maria@cautlocuinta.ro" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4733" />
                      </div>
                    </div>

                    {/* Tagline */}
                    <div data-id="element-4734">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4735">
                        Mesaj personal (opțional)
                      </label>
                      <input type="text" value={agentData.tagline} onChange={e => setAgentData(prev => ({
                    ...prev,
                    tagline: e.target.value
                  }))} placeholder="ex: Te ajut cu tot procesul de închiriere/vânzare" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#357BEB] focus:ring-0 outline-none transition-colors" data-id="element-4736" />
                    </div>

                    {/* Branding Preview */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-4737">
                      <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-4738">
                        Branding prezentare
                      </p>
                      <div className="flex items-center space-x-3 mb-4" data-id="element-4739">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t2c7prQZxrCZkJrsCYknVo/logo-clc-horizontal.png" alt="CautLocuinta.ro" className="h-8" onError={e => {
                      e.currentTarget.style.display = 'none';
                    }} data-id="element-4740" />
                        <span className="text-lg font-bold text-[#132C53]" data-id="element-4741">
                          CautLocuinta.ro
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-4742">
                        Logo-ul CautLocuinta.ro va fi afișat automat în
                        prezentare
                      </p>
                    </div>

                    {/* Contact Display Options */}
                    <div className="space-y-3" data-id="element-4743">
                      <p className="text-sm font-semibold text-gray-700" data-id="element-4744">
                        Opțiuni afișare contact
                      </p>
                      <label className="flex items-center space-x-3 cursor-pointer" data-id="element-4745">
                        <input type="checkbox" checked={agentData.showContactHeader} onChange={e => setAgentData(prev => ({
                      ...prev,
                      showContactHeader: e.target.checked
                    }))} className="w-5 h-5 rounded border-gray-300 text-[#357BEB] focus:ring-[#357BEB]" data-id="element-4746" />
                        <span className="text-sm text-gray-700" data-id="element-4747">
                          Include datele mele de contact în headerul prezentării
                        </span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer" data-id="element-4748">
                        <input type="checkbox" checked={agentData.showContactFooter} onChange={e => setAgentData(prev => ({
                      ...prev,
                      showContactFooter: e.target.checked
                    }))} className="w-5 h-5 rounded border-gray-300 text-[#357BEB] focus:ring-[#357BEB]" data-id="element-4749" />
                        <span className="text-sm text-gray-700" data-id="element-4750">
                          Include datele mele de contact în footerul prezentării
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <button onClick={handleGeneratePresentation} disabled={!canProceedStep3 || isLoading} className={`mt-8 w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center space-x-2 ${!canProceedStep3 || isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-lg hover:scale-[1.02]'}`} data-id="element-4751">
                    {isLoading ? <>
                        <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-4752" />
                        <span data-id="element-4753">Se generează prezentarea...</span>
                      </> : <>
                        <SparklesIcon className="w-5 h-5" data-id="element-4754" />
                        <span data-id="element-4755">Generează prezentarea</span>
                      </>}
                  </button>
                </div>

                {/* Navigation */}
                <div className="flex justify-start mt-6" data-id="element-4756">
                  <button onClick={() => setCurrentStep(2)} className="flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-4757">
                    <ArrowLeftIcon className="w-5 h-5" data-id="element-4758" />
                    <span data-id="element-4759">Înapoi</span>
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* Step 4: Generated Presentation */}
          {currentStep === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-4760">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-4761">
                <div className="bg-gradient-to-r from-[#21A366] to-[#357BEB] p-6 sm:p-8 text-white" data-id="element-4762">
                  <div className="flex items-center space-x-3" data-id="element-4763">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center" data-id="element-4764">
                      <CheckCircleIcon className="w-6 h-6" data-id="element-4765" />
                    </div>
                    <div data-id="element-4766">
                      <h2 className="text-xl sm:text-2xl font-bold" data-id="element-4767">
                        Prezentarea a fost generată!
                      </h2>
                      <p className="text-white/90 text-sm sm:text-base mt-1" data-id="element-4768">
                        Poți vizualiza, partaja sau exporta prezentarea de mai
                        jos.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 sm:p-8" data-id="element-4769">
                  {/* Preview & Actions Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4770">
                    {/* Preview - Takes 2 columns */}
                    <div className="lg:col-span-2" data-id="element-4771">
                      <h3 className="text-lg font-bold text-[#132C53] mb-4" data-id="element-4772">
                        Preview prezentare
                      </h3>
                      <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50" data-id="element-4773">
                        <div className="aspect-[4/3] overflow-y-auto max-h-[600px]" data-id="element-4774">
                          {/* Embedded Preview */}
                          <PropertyPresentationPreviewEmbed propertyData={propertyData} agentData={agentData} photos={selectedPhotos} data-id="element-4775" />
                        </div>
                      </div>
                    </div>

                    {/* Actions - Takes 1 column */}
                    <div className="space-y-4" data-id="element-4776">
                      <h3 className="text-lg font-bold text-[#132C53] mb-4" data-id="element-4777">
                        Acțiuni
                      </h3>

                      {/* Copy Link */}
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-4778">
                        <p className="text-sm font-semibold text-[#132C53] mb-2" data-id="element-4779">
                          Link de preview
                        </p>
                        <p className="text-xs text-gray-600 mb-3" data-id="element-4780">
                          Acest link poate fi trimis proprietarului sau altor
                          agenți pentru vizualizare.
                        </p>
                        <div className="flex items-center space-x-2" data-id="element-4781">
                          <input type="text" readOnly value={`https://pro.cautlocuinta.ro/prezentare/${presentationId}`} className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700" data-id="element-4782" />
                          <button onClick={() => {
                        navigator.clipboard.writeText(`https://pro.cautlocuinta.ro/prezentare/${presentationId}`);
                      }} className="px-4 py-2 bg-[#357BEB] text-white rounded-lg text-sm font-semibold hover:bg-[#2868d8] transition-colors whitespace-nowrap" data-id="element-4783">
                            Copiază
                          </button>
                        </div>
                      </div>

                      {/* Export HTML */}
                      <div className="bg-green-50 rounded-xl p-4 border border-green-200" data-id="element-4784">
                        <p className="text-sm font-semibold text-[#132C53] mb-2" data-id="element-4785">
                          Export HTML
                        </p>
                        <p className="text-xs text-gray-600 mb-3" data-id="element-4786">
                          Descarcă prezentarea ca fișier HTML pentru embed în
                          email sau website.
                        </p>
                        <button className="w-full px-4 py-2 bg-[#21A366] text-white rounded-lg text-sm font-semibold hover:bg-[#1a8555] transition-colors" data-id="element-4787">
                          Descarcă HTML
                        </button>
                      </div>

                      {/* Open in New Tab */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-4788">
                        <p className="text-sm font-semibold text-[#132C53] mb-2" data-id="element-4789">
                          Vizualizare completă
                        </p>
                        <p className="text-xs text-gray-600 mb-3" data-id="element-4790">
                          Deschide prezentarea într-o fereastră nouă pentru
                          vizualizare completă.
                        </p>
                        <button className="w-full px-4 py-2 bg-[#132C53] text-white rounded-lg text-sm font-semibold hover:bg-[#1e4278] transition-colors" data-id="element-4791">
                          Deschide în tab nou
                        </button>
                      </div>

                      {/* Create New */}
                      <button onClick={() => {
                    setCurrentStep(1);
                    setListingUrl('');
                    setImportSuccess(false);
                    setPropertyData({
                      title: '',
                      address: '',
                      city: '',
                      zone: '',
                      propertyType: 'Apartament',
                      rooms: '',
                      surface: '',
                      floor: '',
                      price: '',
                      description: '',
                      features: []
                    });
                    setPhotos([]);
                    setAgentData({
                      name: '',
                      phone: '',
                      email: '',
                      photo: null,
                      tagline: '',
                      showContactHeader: true,
                      showContactFooter: true
                    });
                    setPresentationId(null);
                  }} className="w-full px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors" data-id="element-4792">
                        Creează o nouă prezentare
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
// Embedded Preview Component
function PropertyPresentationPreviewEmbed({
  propertyData,
  agentData,
  photos
}: {
  propertyData: PropertyData;
  agentData: AgentData;
  photos: ImportedPhoto[];
}) {
  return <div className="bg-white" data-id="element-4793">
      {/* Header */}
      <div className="bg-[#132C53] text-white p-4 sm:p-6" data-id="element-4794">
        <div className="flex items-center justify-between" data-id="element-4795">
          <div className="flex items-center space-x-3" data-id="element-4796">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-4797">
              <span className="text-[#132C53] font-bold text-sm" data-id="element-4798">CLC</span>
            </div>
            <span className="font-bold" data-id="element-4799">CautLocuinta.ro</span>
          </div>
          {agentData.showContactHeader && agentData.name && <div className="text-right text-sm" data-id="element-4800">
              <p className="font-semibold" data-id="element-4801">{agentData.name}</p>
              <p className="text-white/80" data-id="element-4802">{agentData.phone}</p>
            </div>}
        </div>
      </div>

      {/* Hero Image */}
      {photos.length > 0 && <div className="relative h-48 sm:h-64" data-id="element-4803">
          <img src={photos[0].url} alt="" className="w-full h-full object-cover" data-id="element-4804" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-4805" />
          <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-4806">
            <h1 className="text-xl sm:text-2xl font-bold mb-1" data-id="element-4807">
              {propertyData.title}
            </h1>
            <p className="text-sm text-white/90 flex items-center" data-id="element-4808">
              <MapPinIcon className="w-4 h-4 mr-1" data-id="element-4809" />
              {propertyData.city}
              {propertyData.zone && `, ${propertyData.zone}`}
            </p>
          </div>
        </div>}

      {/* Price & Details */}
      <div className="p-4 sm:p-6" data-id="element-4810">
        <div className="flex items-center justify-between mb-6" data-id="element-4811">
          <div className="text-3xl font-bold text-[#357BEB]" data-id="element-4812">
            €{Number(propertyData.price).toLocaleString('ro-RO')}
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-4813">
            {propertyData.rooms && <span className="flex items-center" data-id="element-4814">
                <BedDoubleIcon className="w-4 h-4 mr-1" data-id="element-4815" />
                {propertyData.rooms} camere
              </span>}
            {propertyData.surface && <span className="flex items-center" data-id="element-4816">
                <RulerIcon className="w-4 h-4 mr-1" data-id="element-4817" />
                {propertyData.surface} mp
              </span>}
          </div>
        </div>

        {/* Photo Grid */}
        {photos.length > 1 && <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-4818">
            {photos.slice(1, 7).map(photo => <img key={photo.id} src={photo.url} alt="" className="w-full h-20 object-cover rounded-lg" data-id="element-4819" />)}
          </div>}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-4820">
          {[{
          label: 'Tip',
          value: propertyData.propertyType
        }, {
          label: 'Camere',
          value: propertyData.rooms
        }, {
          label: 'Suprafață',
          value: propertyData.surface ? `${propertyData.surface} mp` : ''
        }, {
          label: 'Etaj',
          value: propertyData.floor
        }].filter(item => item.value).map((item, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-3" data-id="element-4821">
                <p className="text-xs text-gray-500" data-id="element-4822">{item.label}</p>
                <p className="font-semibold text-[#132C53]" data-id="element-4823">{item.value}</p>
              </div>)}
        </div>

        {/* Description */}
        {propertyData.description && <div className="mb-6" data-id="element-4824">
            <h3 className="font-bold text-[#132C53] mb-2" data-id="element-4825">Descriere</h3>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4826">
              {propertyData.description}
            </p>
          </div>}

        {/* Features */}
        {propertyData.features.length > 0 && <div className="mb-6" data-id="element-4827">
            <h3 className="font-bold text-[#132C53] mb-2" data-id="element-4828">Facilități</h3>
            <div className="flex flex-wrap gap-2" data-id="element-4829">
              {propertyData.features.map((feature, idx) => <span key={idx} className="px-3 py-1 bg-blue-50 text-[#357BEB] rounded-full text-sm" data-id="element-4830">
                  {feature}
                </span>)}
            </div>
          </div>}

        {/* Agent Contact */}
        {agentData.showContactFooter && agentData.name && <div className="bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-xl p-4 text-white" data-id="element-4831">
            <div className="flex items-center space-x-4" data-id="element-4832">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center overflow-hidden" data-id="element-4833">
                {agentData.photo ? <img src={agentData.photo} alt="" className="w-full h-full object-cover" data-id="element-4834" /> : <UserIcon className="w-7 h-7 text-white" data-id="element-4835" />}
              </div>
              <div className="flex-1" data-id="element-4836">
                <p className="font-bold" data-id="element-4837">{agentData.name}</p>
                {agentData.tagline && <p className="text-sm text-white/80" data-id="element-4838">{agentData.tagline}</p>}
                <div className="flex items-center space-x-3 mt-1 text-sm" data-id="element-4839">
                  <span className="flex items-center" data-id="element-4840">
                    <PhoneIcon className="w-3 h-3 mr-1" data-id="element-4841" />
                    {agentData.phone}
                  </span>
                  {agentData.email && <span className="flex items-center" data-id="element-4842">
                      <MailIcon className="w-3 h-3 mr-1" data-id="element-4843" />
                      {agentData.email}
                    </span>}
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 p-4 text-center" data-id="element-4844">
        <p className="text-xs text-gray-500" data-id="element-4845">
          Prezentare generată de <strong data-id="element-4846">CautLocuinta.ro</strong>
        </p>
      </div>
    </div>;
}